import { Auth } from '@billda/permissions-ui';
import type { TokenRefreshConfig } from '../types/config';

/**
 * Check if a token is near expiry and should be proactively refreshed.
 *
 * A token is considered "near expiry" if:
 * 1. It's already expired, OR
 * 2. It will expire within the configured buffer time (default: 10 seconds)
 *
 * This function is used for proactive token refresh in the request interceptor
 * to avoid making requests with tokens that are about to expire.
 *
 * @param token The JWT access token to check
 * @param config Token refresh configuration containing the buffer time
 * @returns True if the token should be refreshed, false otherwise
 *
 * @example
 * ```typescript
 * if (isTokenNearExpiry(token, config)) {
 *   // Proactively refresh the token before making the request
 *   await handleTokenRefresh();
 * }
 * ```
 */
export function isTokenNearExpiry(
  token: string,
  config: TokenRefreshConfig
): boolean {
  return isTokenWithinExpiryBuffer(token, config.tokenRefreshBuffer);
}

/**
 * Check if a token is within `bufferSeconds` of expiry (or already expired).
 *
 * Same logic as {@link isTokenNearExpiry} but takes the buffer directly, so
 * callers that only have a buffer (not a full `TokenRefreshConfig`) don't need
 * to fabricate a config object.
 *
 * @param token The JWT access token to check
 * @param bufferSeconds Refresh this many seconds before expiry
 * @returns True if the token should be refreshed, false otherwise
 */
export function isTokenWithinExpiryBuffer(
  token: string,
  bufferSeconds: number
): boolean {
  // Check if token is already expired
  if (Auth.isTokenExpired(token)) {
    return true;
  }

  // Parse token to get expiration time
  const parsed = Auth.parseToken(token);
  if (!parsed?.expiresAt) {
    return true; // Can't determine expiry, assume it needs refresh
  }

  // Calculate time until expiry
  const currentTime = Math.floor(Date.now() / 1000);
  const expSeconds = Math.floor(parsed.expiresAt.getTime() / 1000);
  const timeUntilExpiry = expSeconds - currentTime;

  // Check if within refresh buffer
  return timeUntilExpiry <= bufferSeconds;
}
